import { useCallback, useMemo } from 'react';
import { useAsyncOptions } from './useAsyncOptions';
import userService from '../services/userService';
import authService from '../auth/authService';
import type { AsyncOption } from './useAsyncOptions';
import type { User } from '../types/user.types';

interface UseUserFilterOptionsConfig {
  facilityId?: string | number;
  /**
   * Current users data to extract initial options from
   */
  users?: User[];
}

/**
 * Custom hook for managing user filter dropdown options with API-based search
 */
export function useUserFilterOptions(config: UseUserFilterOptionsConfig = {}) {
  const { facilityId, users = [] } = config;

  // Get effective facility ID (memoized)
  const effectiveFacilityId = useMemo(() => {
    if (facilityId) {
      return facilityId;
    }
    const currentUser = authService.getUser();
    const storedFacilityId = authService.getFacilityId();
    const facilityFromObject = authService.getFacility();
    const effectiveId =
      storedFacilityId || (facilityFromObject ? String(facilityFromObject.id) : undefined);
    const isRisAdmin = currentUser?.roles?.some(role => role === 'ris-admin') ?? false;
    return isRisAdmin && effectiveId ? effectiveId : undefined;
  }, [facilityId]);

  // Extract initial options from current page data
  const initialNameOptions = useMemo(() => {
    const names = Array.from(new Set(users.map(u => u.full_name).filter(Boolean)));
    return names.map(name => ({ value: name, label: name }));
  }, [users]);

  const initialRoleOptions = useMemo(() => {
    const roles = Array.from(new Set(users.map(u => u.role).filter(Boolean)));
    return roles.map(role => ({ value: role, label: role }));
  }, [users]);

  const initialEmployeeIdOptions = useMemo(() => {
    const employeeIds = Array.from(
      new Set(
        users
          .map(u => {
            return u.role === 'Radiologist' ? u.doctor_id : u.employee_id;
          })
          .filter((id): id is string => Boolean(id))
      )
    );
    return employeeIds.map(id => ({ value: id, label: id }));
  }, [users]);

  const initialPhoneNumberOptions = useMemo(() => {
    const phoneNumbers = Array.from(new Set(users.map(u => u.mobile_number).filter(Boolean)));
    return phoneNumbers.map(phone => ({ value: phone, label: phone }));
  }, [users]);

  // Name filter options
  const nameOptions = useAsyncOptions({
    fetchOptions: useCallback(
      async (query: string): Promise<AsyncOption[]> => {
        return userService.searchUserOptions('name', query, effectiveFacilityId);
      },
      [effectiveFacilityId]
    ),
    debounceDelay: 500,
    minSearchLength: 0,
    initialOptions: initialNameOptions,
  });

  // Role filter options
  const roleOptions = useAsyncOptions({
    fetchOptions: useCallback(
      async (query: string): Promise<AsyncOption[]> => {
        return userService.searchUserOptions('role', query, effectiveFacilityId);
      },
      [effectiveFacilityId]
    ),
    debounceDelay: 500,
    minSearchLength: 0,
    initialOptions: initialRoleOptions,
  });

  // Employee ID filter options
  const employeeIdOptions = useAsyncOptions({
    fetchOptions: useCallback(
      async (query: string): Promise<AsyncOption[]> => {
        return userService.searchUserOptions('employee_id', query, effectiveFacilityId);
      },
      [effectiveFacilityId]
    ),
    debounceDelay: 500,
    minSearchLength: 0,
    initialOptions: initialEmployeeIdOptions,
  });

  // Phone number filter options
  const phoneNumberOptions = useAsyncOptions({
    fetchOptions: useCallback(
      async (query: string): Promise<AsyncOption[]> => {
        return userService.searchUserOptions('phone_number', query, effectiveFacilityId);
      },
      [effectiveFacilityId]
    ),
    debounceDelay: 500,
    minSearchLength: 0,
    initialOptions: initialPhoneNumberOptions,
  });

  // Status options (static)
  const statusOptions: AsyncOption[] = userService.getStatusOptions();

  return {
    nameOptions,
    roleOptions,
    employeeIdOptions,
    phoneNumberOptions,
    statusOptions,
  };
}
